import type { Agorot } from './money';
import type { IsoDate, YearMonth } from './dates';
import type { Worker } from './people';

/**
 * What a payment line is, for checks that count only some components.
 * The minimum wage check counts `base` and `fixed` only; overtime, shift
 * premiums, bonuses, seniority and expense refunds are excluded.
 * Parsers assign this from their vendor label maps. A label the parser
 * doesn't recognize is `unknown` (and a missing kind is treated the same):
 * rules must treat it as uncertainty (`cannot_check` or lower confidence),
 * never silently exclude it. `other` means recognized but not relevant to
 * any check.
 */
export type PaymentKind =
  | 'base'
  | 'fixed'
  | 'overtime'
  /** Fixed monthly overtime payment (שעות נוספות גלובליות). */
  | 'globalOvertime'
  | 'shift'
  | 'bonus'
  /** Sales commissions (עמלות): output-linked pay, part of the regular wage per the courts (docs/research/commission.md). Uncertain for minimum wage and pension. */
  | 'commission'
  | 'seniority'
  | 'expense'
  | 'travel'
  | 'recuperation'
  /** Payment for unused vacation days cashed out during employment (תמורת חופשה). Excluded from minimum wage and pensionable wage. */
  | 'vacationCashOut'
  /** Sick pay (דמי מחלה): wages for sick days. Uncertain for minimum wage and pension (pensionable-wage open item). */
  | 'sickPay'
  /** Holiday pay (דמי חגים): wages for public holidays. Uncertain for minimum wage and pension, like sick pay. */
  | 'holidayPay'
  /** Reserve-duty pay (מילואים): the employer's advance, reimbursed by National Insurance. Uncertain for minimum wage and pension. */
  | 'reserveDuty'
  /** Back pay (הפרשי שכר): wage differences for earlier months the payslip doesn't name. Always uncertain (persona M10). */
  | 'backPay'
  /** Severance paid on the payslip (פיצויי פיטורים): not wage; counted as severance paid by the severance check. */
  | 'severancePay'
  /** Pay in lieu of notice (תמורת הודעה מוקדמת): not wage for the month; counted by the notice check. */
  | 'noticePay'
  | 'other'
  | 'unknown';

export type PayBasis = 'monthly' | 'hourly';

export interface PaymentLine {
  /** Vendor's line code, as printed. */
  code: string;
  /** Label as printed (Hebrew labels are data, not logic). */
  label: string;
  kind?: PaymentKind;
  quantity?: number;
  /** Amount per unit of `quantity`. */
  rate?: Agorot;
  amount: Agorot;
  /** Set when the line pays for a different month (e.g. a retroactive correction). */
  periodOverride?: YearMonth;
}

/**
 * What a deduction line (other than tax, National Insurance, health tax and pension) is, for the foreign-worker
 * deduction caps (Foreign Workers regulations). `depositEmployee`: the deposit (פיקדון) taken from the wage.
 * `unknown`: a label the parser doesn't recognize (uncertainty, never ignored).
 */
export type DeductionLineKind = 'housing' | 'ancillary' | 'healthInsurance' | 'food' | 'depositEmployee' | 'other' | 'unknown';

export interface DeductionLine {
  /** Label as printed (Hebrew labels are data, not logic). */
  label: string;
  kind: DeductionLineKind;
  amount: Agorot;
}

/**
 * A payments-table row that prints a taxable value only ("שווי למס") and no amount: the value of a benefit (a car, a phone)
 * or, on some payslips, income from another workplace counted for tax. Not cash paid, so never a payment line and never
 * summed as one; shown in the translated view (src/translate) only.
 */
export interface TaxableValueLine {
  /** Vendor's line code, as printed. */
  code: string;
  /** Label as printed (Hebrew labels are data, not logic). */
  label: string;
  value: Agorot;
}

export interface OvertimeLine {
  /** 125, 150, … */
  ratePercent: number;
  hours: number;
  amount: Agorot;
}

/** Leave balances, in days (or hours if the payslip uses hours; parsers normalize to days). */
export interface LeaveBalance {
  opening?: number;
  accrued?: number;
  used?: number;
  closing?: number;
}

export interface YearToDate {
  gross?: Agorot;
  incomeTax?: Agorot;
  nationalInsurance?: Agorot;
  healthTax?: Agorot;
  pensionEmployee?: Agorot;
}

/**
 * One payslip, normalized from any vendor format. Every field is optional:
 * a check whose inputs are missing returns a `cannot_check` finding.
 * Percentages are 0–100 numbers (e.g. jobPercent 50 = half-time).
 */
export interface PayslipFields {
  employerName?: string;
  /**
   * Whose payslip: the worker's ID number (and name), normalised (src/model/people.ts). Only to keep different people's
   * payslips apart, on the device: never shown, stored, sent, logged or put in a report.
   */
  worker?: Worker;
  employerCompanyId?: string;
  deductionsFileNumber?: string;
  payMonth?: YearMonth;
  startDate?: IsoDate;
  /**
   * A seniority date the payslip prints ("וותק", Michpal). Not the start date: seniority can be credited from
   * earlier. The app offers it in the start-date question and uses it only once the user confirms it.
   */
  seniorityDate?: IsoDate;
  /**
   * Monthly-salaried vs paid by the hour; decides which minimum wage applies.
   * Set by the parser only when the payslip makes it clear; `inferred` is
   * true when deduced (e.g. from the line structure) rather than printed.
   * `UserAnswers.payBasis` overrides it; see `resolvePayBasis`.
   */
  payBasis?: { value: PayBasis; inferred: boolean };
  jobPercent?: number;
  /**
   * Hours actually worked in the month (Shiklulit "שעות עבודה" → בפועל). Equal to `hoursActual` when
   * the payslip prints both; the checks use this one.
   */
  hoursWorked?: number;
  /**
   * Shiklulit prints "שעות עבודה" as "משולמות / בפועל": hours paid and hours actually worked. The text
   * reads "actual/paid" (the Hebrew labels run right to left, the numbers left to right), confirmed on
   * one employer's 12 digital payslips: paid − actual equals the vacation-pay line's quantity in 10.
   * The gap can be hours paid on other lines (vacation, holiday, sick): recorded in the minimum-wage
   * steps only, never a finding (accountant open item).
   */
  hoursPaid?: number;
  hoursActual?: number;
  standardHours?: number;
  /** The employer's standard work days in a full month, as printed (Shiklulit "ימי תקן"): a 5- or 6-day week (`resolveWorkWeek`). */
  standardDays?: number;
  daysWorked?: number;
  hourlyRate?: Agorot;
  baseSalary?: Agorot;
  paymentLines?: PaymentLine[];
  overtimeLines?: OvertimeLine[];
  /** Payment lines printed but not read (no amount found, or the table unreadable): the list above is then incomplete, so no rule may answer from its absences. */
  unreadablePaymentLines?: number;
  travelAmount?: Agorot;
  recuperationAmount?: Agorot;
  recuperationDays?: number;
  grossPay?: Agorot;
  grossForTax?: Agorot;
  grossForNI?: Agorot;
  incomeTax?: Agorot;
  marginalTaxRate?: number;
  creditPoints?: number;
  nationalInsurance?: Agorot;
  healthTax?: Agorot;
  pensionEmployee?: Agorot;
  pensionFundName?: string;
  /**
   * The labels printed on the deduction rows these fields were read from, where the label varies by payslip (health tax
   * has several names; pension rows carry the fund's name, joined with " + " when several are added up). Shown as printed
   * in the translated view (src/translate); never used by the checks.
   */
  printedLabels?: Partial<Record<'incomeTax' | 'nationalInsurance' | 'healthTax' | 'pensionEmployee' | 'educationFundEmployee', string>>;
  pensionEmployer?: Agorot;
  severanceEmployer?: Agorot;
  educationFundEmployee?: Agorot;
  educationFundEmployer?: Agorot;
  imputedBenefits?: Agorot;
  /** Payments-table rows with a taxable value only, no amount (TaxableValueLine); display only, read by no check. */
  taxableValueLines?: TaxableValueLine[];
  vacationBalance?: LeaveBalance;
  sickBalance?: LeaveBalance;
  ytd?: YearToDate;
  netPay?: Agorot;
  netToBank?: Agorot;
  /**
   * Deduction lines other than tax, National Insurance, health tax, pension and education fund. Set when the
   * parser read the deductions table (an empty list = none printed); absent = not read.
   */
  deductionLines?: DeductionLine[];
  /** The employer's foreign-worker deposit (פיקדון) for the month, when the payslip prints it. */
  foreignDeposit?: Agorot;
  /** The minimum wage the payslip itself prints (the law requires it); compared with our parameters. */
  printedMinimumWage?: { monthly?: Agorot; hourly?: Agorot };
  /**
   * Mandatory payslip details (Wage Protection Law s.24 and schedule) the parser could check:
   * `checked` = the parser found the detail's label; `absent` = the label is printed but its value
   * is empty. A detail whose label isn't found is in neither list (it may be printed under a label
   * the parser doesn't know), so it's never flagged. Set only by vendor parsers.
   */
  mandatoryDetails?: { checked: MandatoryDetail[]; absent: MandatoryDetail[] };
  /**
   * Pension fields that may be printed but weren't read: OCR found no value, or a value outside the
   * plausible range was held back until the user vouches for it. Unlike a field the parser knows is
   * absent (= not on the payslip), these are unknown: the pension check says it can't check them,
   * never that nothing was paid.
   */
  notRead?: NotReadField[];
}

export const NOT_READ_FIELDS = ['pensionEmployee', 'pensionEmployer', 'severanceEmployer'] as const;
export type NotReadField = (typeof NOT_READ_FIELDS)[number];

/** Mandatory payslip details the app can check for (docs/rules-spec.md "Payslip completeness"). */
export const MANDATORY_DETAILS = [
  'employeeName', 'employerName', 'employerId', 'payPeriod', 'startDate', 'jobScope', 'workDays', 'workHours', 'standardHours',
  'vacationBalance', 'sickBalance', 'hourlyRate', 'taxableTotal', 'insurableTotal', 'totalDeductions', 'grossPay', 'netPay',
  'minimumWageMonthly', 'minimumWageHourly',
] as const;
export type MandatoryDetail = (typeof MANDATORY_DETAILS)[number];
